import { TRACKING_EVENTS, type TrackingEventName } from "@/lib/analytics"
import {
  TIMESTAMP_MODE,
  type ConversationsTableResult,
  type TimestampMode,
} from "@/types/tables/ConversationsTable"
import { downloadBlob } from "@/utils/download"
import { resolveMetricDisplayName } from "@/utils/formatting/format-metric-display-results"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

/**
 * Columns whose cell is an interactive widget rather than a value — there is
 * nothing meaningful to write into a spreadsheet, so they are left out of the
 * export instead of emitting an empty column.
 */
const NON_EXPORTABLE_CONVERSATION_COLUMNS = new Set(["audio"])

interface MetricDisplayRecord {
  metric_display_name?: string | null
}

interface DrillDownRunRecordLike {
  simulation_output_id: string
  occurred_at: string
  created_at?: string | null
  value: string | number
}

export interface TableCsvColumn<T> {
  header: string
  value: (row: T) => unknown
}

interface ConversationCsvColumn {
  key: string
  label: string
}

/**
 * Convert monitoring data to CSV format matching the visible table columns
 */
export const convertConversationsDataToCSV = (
  data: ConversationsTableResult[],
  visibleColumns: Set<string>,
  metricRecords: Record<string, MetricDisplayRecord>,
  timestampMode: TimestampMode = TIMESTAMP_MODE.CREATED_AT,
  orderedColumns?: ConversationCsvColumn[],
): string => {
  if (data.length === 0) {
    return ""
  }

  const exportedColumns = (
    orderedColumns?.filter((column) => visibleColumns.has(column.key)) ??
    Array.from(visibleColumns).map((key) => ({ key, label: key }))
  ).filter((column) => !NON_EXPORTABLE_CONVERSATION_COLUMNS.has(column.key))

  // Get visible column headers in the same order as the table
  const headers: string[] = []

  // Add visible columns in order
  exportedColumns.forEach(({ key: columnKey, label }) => {
    if (orderedColumns) {
      headers.push(label)
      return
    }
    if (columnKey.startsWith("metric_")) {
      // For metric columns, use the display name from metric records
      const metricKey = columnKey.replace("metric_", "")
      const displayName = resolveMetricDisplayName(
        metricRecords[metricKey]?.metric_display_name,
        metricKey,
      )
      headers.push(displayName)
    } else if (columnKey === "timestamp") {
      // Use appropriate header based on timestamp mode
      headers.push(
        timestampMode === "occurred_at" ? "Occurred At" : "Created At",
      )
    } else if (columnKey === "reviewer_assignees") {
      headers.push("Reviewer")
    } else {
      // For regular columns, use the column key as header
      headers.push(columnKey)
    }
  })

  // Create CSV header row
  const csvRows = [headers.join(",")]

  // Create data rows
  data.forEach((item) => {
    const row: string[] = []

    exportedColumns.forEach(({ key: columnKey }) => {
      let value: unknown = ""

      if (columnKey === "status") {
        value = item.run_status
      } else if (
        columnKey === "simulationOutputId" ||
        columnKey === "simulation_output_id"
      ) {
        value = item.simulation_output_id
      } else if (columnKey === "timestamp") {
        // Handle timestamp with fallback logic
        const timestamp =
          timestampMode === "occurred_at" && item.occurred_at
            ? item.occurred_at
            : item.created_at
        value = timestamp
      } else if (columnKey === "createdAt") {
        value = item.created_at
      } else if (columnKey === "created_at") {
        value = item.created_at
      } else if (columnKey === "occurred_at") {
        value = item.occurred_at ?? ""
      } else if (columnKey === "customer_metadata") {
        value = JSON.stringify(item.customer_metadata)
      } else if (columnKey === "reviewer_assignees") {
        const reviewerAssignees = item.reviewer_assignees?.join(", ") ?? ""
        value = reviewerAssignees
          ? `${item.reviewer_status === "COMPLETED" ? "Reviewed" : "Assigned"}: ${reviewerAssignees}`
          : ""
      } else if (columnKey === "tags") {
        value = item.tags?.join(", ") ?? ""
      } else if (columnKey === "notes") {
        value = item.notes
      } else if (columnKey.startsWith("metric_")) {
        const metricKey = columnKey.replace("metric_", "")
        value = item.metrics?.[metricKey] ?? ""
      } else if (columnKey.startsWith("explanation_")) {
        const explanationKey = columnKey.replace("explanation_", "")
        value = item.explanations?.[explanationKey] ?? ""
      } else if (columnKey.startsWith("metadata_")) {
        const metadataKey = columnKey.replace("metadata_", "")
        value = item.customer_metadata?.[metadataKey] ?? ""
      }

      // Escape CSV values
      const escapedValue = escapeCSVValue(value)
      row.push(escapedValue)
    })

    csvRows.push(row.join(","))
  })

  return csvRows.join("\n")
}

/**
 * Escape a value for CSV format
 */
const escapeCSVValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ""
  }

  const stringValue = stringifyUnknownValue(value)

  // Check for CSV injection vulnerabilities
  // Values starting with =, +, -, @, tab, or carriage return can be interpreted as formulas
  const csvInjectionPattern = /^[=+\-@\t\r]/
  if (csvInjectionPattern.test(stringValue)) {
    // Prefix with single quote to force text interpretation in spreadsheet software
    const escapedValue = `'${stringValue}`

    // If the value contains comma, quote, newline, or carriage return, wrap it in quotes
    if (
      escapedValue.includes(",") ||
      escapedValue.includes('"') ||
      escapedValue.includes("\n") ||
      escapedValue.includes("\r")
    ) {
      // Escape any existing quotes by doubling them
      const doubleEscapedValue = escapedValue.replace(/"/g, '""')
      return `"${doubleEscapedValue}"`
    }

    return escapedValue
  }

  // If the value contains comma, quote, newline, or carriage return, wrap it in quotes
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n") ||
    stringValue.includes("\r")
  ) {
    // Escape any existing quotes by doubling them
    const escapedValue = stringValue.replace(/"/g, '""')
    return `"${escapedValue}"`
  }

  return stringValue
}

/** Convert table rows using a caller-supplied visible column order. */
export const convertTableDataToCSV = <T>(
  data: T[],
  columns: TableCsvColumn<T>[],
): string => {
  if (data.length === 0 || columns.length === 0) {
    return ""
  }

  const header = columns
    .map((column) => escapeCSVValue(column.header))
    .join(",")
  const rows = data.map((row) =>
    columns.map((column) => escapeCSVValue(column.value(row))).join(","),
  )

  return [header, ...rows].join("\n")
}

/**
 * Column configuration for drill-down tables
 */
export interface DrillDownColumnConfig {
  key: string
  label: string
  isAdditionalMetric?: boolean
  metricId?: string
}

/**
 * Convert drill-down data to CSV format
 */
export const convertDrillDownDataToCSV = (
  rows: DrillDownRunRecordLike[],
  columns: DrillDownColumnConfig[],
  formatTimestamp: (timestamp: string) => string,
  additionalValuesMap?: Map<string, Map<string, string | number>>,
  linkMap?: Map<string, string>,
): string => {
  if (!rows || rows.length === 0) {
    return ""
  }

  // Build CSV headers
  const headers = columns.map((col) => col.label)

  // Add Link column if linkMap is provided
  if (linkMap && linkMap.size > 0) {
    headers.push("Link")
  }

  // Build CSV rows
  const csvRows = rows.map((row: DrillDownRunRecordLike) => {
    const rowData = columns.map((column) => {
      let value: unknown = ""

      switch (column.key) {
        case "simulation_output_id":
          value = row.simulation_output_id
          break
        case "occurred_at":
          value = formatTimestamp(row.occurred_at)
          break
        case "created_at":
          value = row.created_at ? formatTimestamp(row.created_at) : ""
          break
        case "value": {
          const numValue =
            typeof row.value === "string" ? parseFloat(row.value) : row.value
          value = !isNaN(numValue) ? numValue.toFixed(2) : row.value
          break
        }
        default:
          // Handle additional metric columns
          if (
            column.isAdditionalMetric &&
            column.metricId &&
            additionalValuesMap
          ) {
            const metricValue = additionalValuesMap
              .get(row.simulation_output_id)
              ?.get(column.metricId)
            if (metricValue === undefined) {
              value = ""
            } else {
              const numVal =
                typeof metricValue === "string"
                  ? parseFloat(metricValue)
                  : metricValue
              value = !isNaN(numVal) ? numVal.toFixed(2) : metricValue
            }
          } else {
            value =
              (row as unknown as Record<string, unknown>)[column.key] ?? ""
          }
          break
      }

      return escapeCSVValue(value)
    })

    // Add link if available
    if (linkMap) {
      const link = linkMap.get(row.simulation_output_id) || ""
      rowData.push(escapeCSVValue(link))
    }

    return rowData
  })

  // Combine headers and rows
  return [headers.join(","), ...csvRows.map((row) => row.join(","))].join("\n")
}

/**
 * Download CSV data as a file
 */
export const downloadCSV = (
  csvData: string,
  filename: string,
  trackEvent?: (
    eventName: TrackingEventName,
    properties?: Record<string, unknown>,
  ) => void,
): void => {
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" })
  downloadBlob(blob, filename)

  // Track the CSV download event
  if (trackEvent) {
    const rowCount = csvData.split("\n").length - 1 // Subtract header row
    trackEvent(TRACKING_EVENTS.UPLOADED_CONVERSATIONS.DATA_EXPORTED_CSV, {
      filename,
      row_count: rowCount,
      file_size_bytes: blob.size,
    })
  }
}
